import { HStack, VStack } from "@zenchef/styled-system/jsx";
import React, { PropsWithChildren, ReactElement, ReactNode } from "react";
import { isFilterItemComponent } from "./types";
import FilterContext from "./FilterContext";
import Button from "../Button/Button";
import { Text } from "../typography/Text";
import Icon from "../Icon/Icon";
import Drawer, { DrawerProps } from "../Overlay/Drawer/Drawer";
import Filter, { FilterProps } from "./Filter";
import FilterTags from "./FilterTags";
import { FilterItemProps } from "./FilterItem";

interface BaseFilterGroupProps extends PropsWithChildren, FilterProps {
  filterTagsProps?: FilterProps;
  className?: string;
  rootClassName?: string;
}

interface FilterGroupIconOnlyModeProps extends BaseFilterGroupProps {
  mode?: "iconOnly";
  limitUngrouped?: never;
  active?: boolean;
  label?: never;
  drawerProps?: DrawerProps;
}

interface FilterGroupGroupedModeProps extends BaseFilterGroupProps {
  mode?: "grouped";
  limitUngrouped?: never;
  active?: boolean;
  label: string;
  drawerProps?: DrawerProps;
}

interface FilterGroupUngroupedModeProps extends BaseFilterGroupProps {
  mode?: "ungrouped";
  limitUngrouped?: number;
  active?: never;
  label?: never;
  drawerProps?: never;
}

export type FilterGroupProps =
  | FilterGroupIconOnlyModeProps
  | FilterGroupGroupedModeProps
  | FilterGroupUngroupedModeProps;

const FilterGroup = ({
  drawerProps,
  filterTagsProps,
  children,
  className,
  rootClassName,
  ...props
}: FilterGroupProps) => {
  const filters: ReactElement<FilterItemProps, typeof Filter.Item>[] = [];
  const otherComponents: ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (isFilterItemComponent(child)) {
      filters.push(child);
    } else {
      otherComponents.push(child);
    }
  });

  return (
    <>
      <FilterContext.Provider value={{ isGrouped: true }}>
        <VStack alignItems="initial" gap="gap.2" className={rootClassName}>
          <HStack alignItems="initial" gap="gap.2" className={className}>
            <FilterGroupTrigger
              setOpenDrawer={(open) => drawerProps?.onOpenChange?.({ open })}
              filters={filters}
              {...props}
            />
            {otherComponents}
          </HStack>
          {filterTagsProps ? <FilterTags {...filterTagsProps} /> : null}
        </VStack>
      </FilterContext.Provider>
      <Drawer {...drawerProps} />
    </>
  );
};

type FilterGroupTriggerProps = Omit<
  FilterGroupProps,
  "filterTagsProps" | "className" | "rootClassName" | "children"
> & {
  setOpenDrawer: (open: boolean) => void;
  filters: ReactElement<FilterItemProps, typeof Filter.Item>[];
};

const FilterGroupTrigger = ({
  filters,
  mode = "ungrouped",
  limitUngrouped = 4,
  active,
  label,
  disabled,
  setOpenDrawer,
}: FilterGroupTriggerProps) => {
  const handleClick = () => {
    setOpenDrawer(true);
  };

  if (mode === "iconOnly") {
    return (
      <Button
        hierarchy="secondary-subtle"
        iconOnly
        active={active}
        disabled={disabled}
        onClick={handleClick}
      >
        <Icon name="filter-slider" />
      </Button>
    );
  }

  if (mode === "grouped") {
    return (
      <Button
        hierarchy="secondary-subtle"
        active={active}
        disabled={disabled}
        onClick={handleClick}
      >
        <Text>{label}</Text>
        <Icon name="filter-slider" />
      </Button>
    );
  }

  const displayedFilters = filters.slice(0, limitUngrouped);
  const notDisplayedFiltersCount = filters.length - displayedFilters.length;

  return (
    <HStack alignItems="initial" gap="gap.2">
      {displayedFilters}
      {notDisplayedFiltersCount > 0 ? (
        <Button
          hierarchy="secondary-subtle"
          active={active}
          disabled={disabled}
          onClick={handleClick}
        >
          <Text>{label}</Text>
          <Icon name="filter-slider" />
        </Button>
      ) : null}
    </HStack>
  );
};

export default FilterGroup;
